import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import withStyles from 'isomorphic-style-loader/withStyles';
import InfoTooltip from './InfoTooltip';
import GranularitySelect from './GranularitySelect';
import { formatMoney, formatSeriesLabel, formatDay } from './reportingFormat';
import { readChartTheme, withAlpha } from './chartTheme';
import messages from '../../../locale/jsonMessages';
import s from './OwnerReporting.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

// "Evolution of my net revenue": line with area. series = { granularity, points: [{ key, start, end, netRevenue }] } as returned by
// the server (points without revenue are already 0). Colors come from the theme variables at run time.
const EvolutionChart = ({ series, granularity, onGranularityChange, currency, help, title }) => {
  const { formatMessage, locale } = useIntl();
  const [theme, setTheme] = useState(null);
  useEffect(() => { setTheme(readChartTheme()); }, []);

  const points = (series && series.points) || [];
  const spansYears = useMemo(() => new Set(points.map((point) => String(point.start || '').slice(0, 4))).size > 1, [points]);
  const labels = points.map((point) => formatSeriesLabel(point, granularity, locale, { withYear: spansYears && granularity !== 'year' && granularity !== 'day' }));

  const line = theme && theme.line;
  const data = {
    labels,
    datasets: [{
      label: formatMessage(messages.orpSeriesLabel),
      data: points.map((point) => Number(point.netRevenue) || 0),
      borderColor: line || undefined,
      backgroundColor: line ? withAlpha(line, 0.12) : undefined,
      pointBackgroundColor: (theme && theme.pointFill) || undefined,
      pointBorderColor: line || undefined,
      fill: true,
      tension: 0.25,
      pointRadius: points.length > 40 ? 0 : 3,
      pointHoverRadius: 5,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: (theme && theme.tooltipBg) || undefined,
        titleColor: (theme && theme.tooltipText) || undefined,
        bodyColor: (theme && theme.tooltipText) || undefined,
        callbacks: {
          title: (items) => {
            const point = points[items[0] && items[0].dataIndex];
            if (!point) return '';
            return granularity === 'day' || granularity === 'week'
              ? `${formatDay(point.start, locale, 'medium')}${point.end && point.end !== point.start ? ` - ${formatDay(point.end, locale, 'medium')}` : ''}`
              : formatSeriesLabel(point, granularity, locale, { withYear: true });
          },
          label: (context) => formatMoney(context.raw, currency, locale),
        },
      },
    },
    scales: {
      x: { grid: { color: (theme && theme.grid) || undefined }, ticks: { color: (theme && theme.ticks) || undefined, maxRotation: 0, autoSkip: true } },
      y: {
        beginAtZero: true,
        grid: { color: (theme && theme.grid) || undefined },
        ticks: { color: (theme && theme.ticks) || undefined, callback: (value) => new Intl.NumberFormat(locale, { notation: 'compact' }).format(value) },
      },
    },
  };

  return (
    <div className={s.panel}>
      <h3 className={s.panelTitle}>
        <span className={s.panelTitleText}>{title || formatMessage(messages.orpEvolutionTitle)}<InfoTooltip text={help} /></span>
        <GranularitySelect value={granularity} onChange={onGranularityChange} />
      </h3>
      <div className={s.chartBox}>
        {theme && points.length > 0
          ? <Line data={data} options={options} />
          : <div className={s.chartPlaceholder}>{points.length === 0 ? formatMessage(messages.orpChartNoData) : ''}</div>}
      </div>
    </div>
  );
};

export default withStyles(s)(EvolutionChart);
